// pages/Stock.js - Updated with better real-time updates

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, onValue, update, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import "../styles/main.css";

export default function Stock() {
  const navigate = useNavigate();
  
  // State for items
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStockStatus, setSelectedStockStatus] = useState("all");
  
  // State for stock adjustment
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState("add");
  const [adjustmentQty, setAdjustmentQty] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  
  // State for categories
  const [categories, setCategories] = useState([]);
  
  // Statistics
  const [totalItems, setTotalItems] = useState(0);
  const [totalStockValue, setTotalStockValue] = useState(0);
  const [lowStockItems, setLowStockItems] = useState(0);
  const [outOfStockItems, setOutOfStockItems] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Load items from Firebase with real-time updates
  useEffect(() => {
    const itemsRef = ref(database, "items");
    
    const unsubscribe = onValue(itemsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const itemList = Object.entries(data).map(([id, item]) => ({
          id,
          ...item,
          currentStock: item.currentStock || 0,
          minimumStock: item.minimumStock || 0,
          maximumStock: item.maximumStock || 0,
          sellingPrice: Number(item.sellingPrice) || 0,
          purchasePrice: Number(item.purchasePrice) || 0,
          category: item.category || "Uncategorized",
          unit: item.unit || "NOS"
        }));
        
        // Sort by name
        itemList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        
        setItems(itemList);
        setLastUpdated(new Date().toLocaleTimeString());
        
        // Extract unique categories
        const uniqueCategories = [...new Set(itemList.map(item => item.category))];
        setCategories(uniqueCategories);
        
        // Calculate statistics
        const totalValue = itemList.reduce((sum, item) => sum + ((item.currentStock || 0) * (item.purchasePrice || 0)), 0);
        setTotalStockValue(totalValue);
        setTotalItems(itemList.length);
        
        const lowStock = itemList.filter(item => item.currentStock > 0 && item.currentStock <= item.minimumStock).length;
        setLowStockItems(lowStock);
        
        const outOfStock = itemList.filter(item => item.currentStock === 0).length;
        setOutOfStockItems(outOfStock);
        
      } else {
        setItems([]);
        setTotalItems(0);
        setTotalStockValue(0);
        setLowStockItems(0);
        setOutOfStockItems(0);
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Filter items based on search, category, and stock status
  useEffect(() => {
    let filtered = [...items];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.hsn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    
    // Stock status filter
    if (selectedStockStatus === "low") {
      filtered = filtered.filter(item => item.currentStock > 0 && item.currentStock <= item.minimumStock);
    } else if (selectedStockStatus === "out") {
      filtered = filtered.filter(item => item.currentStock === 0);
    } else if (selectedStockStatus === "normal") {
      filtered = filtered.filter(item => item.currentStock > item.minimumStock);
    }
    
    setFilteredItems(filtered);
  }, [items, searchTerm, selectedCategory, selectedStockStatus]);
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2
    }).format(amount || 0);
  };
  
  // Get stock status
  const getStockStatus = (item) => {
    if (item.currentStock === 0) {
      return { status: "Out of Stock", color: "#dc3545", icon: "❌", class: "out-of-stock" };
    } else if (item.currentStock <= item.minimumStock) {
      return { status: "Low Stock", color: "#ff9800", icon: "⚠️", class: "low-stock" };
    } else {
      return { status: "In Stock", color: "#28a745", icon: "✅", class: "in-stock" };
    }
  };
  
  // Handle stock adjustment
  const handleAdjustStock = async () => {
    if (!selectedItem) return;
    if (!adjustmentQty || parseFloat(adjustmentQty) <= 0) {
      alert("Please enter a valid quantity");
      return;
    }
    
    setAdjustmentLoading(true);
    
    try {
      const qty = parseFloat(adjustmentQty);
      const itemRef = ref(database, `items/${selectedItem.id}`);
      let newStock;
      let adjustmentNote = "";
      
      switch (adjustmentType) {
        case "add":
          newStock = (selectedItem.currentStock || 0) + qty;
          adjustmentNote = `Added ${qty} ${selectedItem.unit}`;
          break;
        case "remove":
          if (qty > (selectedItem.currentStock || 0)) {
            alert("Cannot remove more than current stock");
            setAdjustmentLoading(false);
            return;
          }
          newStock = (selectedItem.currentStock || 0) - qty;
          adjustmentNote = `Removed ${qty} ${selectedItem.unit}`;
          break;
        case "set":
          newStock = qty;
          adjustmentNote = `Set stock to ${qty} ${selectedItem.unit}`;
          break;
        default:
          newStock = (selectedItem.currentStock || 0) + qty;
      }
      
      const timestamp = new Date().toISOString();
      
      // Update item stock
      await update(itemRef, {
        currentStock: newStock,
        lastStockUpdate: timestamp,
        lastAdjustmentType: adjustmentType,
        lastAdjustmentQty: qty,
        updatedAt: timestamp
      });
      
      // Record stock adjustment in history
      const adjustmentRef = ref(database, `stockHistory/${selectedItem.id}`);
      const historySnapshot = await get(adjustmentRef);
      const existingHistory = historySnapshot.exists() ? historySnapshot.val() : [];
      const historyList = Array.isArray(existingHistory) ? existingHistory : [];
      
      const newAdjustment = {
        id: Date.now(),
        date: timestamp,
        type: adjustmentType,
        previousStock: selectedItem.currentStock || 0,
        newStock: newStock,
        quantity: qty,
        reason: adjustmentReason || adjustmentNote,
        user: "Admin",
        note: adjustmentReason
      };
      
      historyList.unshift(newAdjustment);
      
      // Keep only last 50 adjustments
      if (historyList.length > 50) {
        historyList.pop();
      }
      
      await update(adjustmentRef, historyList);
      
      alert(`Stock updated successfully! ${selectedItem.name} stock changed from ${selectedItem.currentStock} to ${newStock} ${selectedItem.unit}`);
      
      // Reset modal
      setShowAdjustModal(false);
      setSelectedItem(null);
      setAdjustmentQty("");
      setAdjustmentReason("");
      
    } catch (error) {
      console.error("Error adjusting stock:", error);
      alert("Failed to update stock. Please try again.");
    } finally {
      setAdjustmentLoading(false);
    }
  };
  
  // Open adjustment modal
  const openAdjustModal = (item) => {
    setSelectedItem(item);
    setAdjustmentType("add");
    setAdjustmentQty("");
    setAdjustmentReason("");
    setShowAdjustModal(true);
  };
  
  // Navigate to item edit page
  const handleEditItem = (item) => {
    navigate(`/items/edit/${item.id}`);
  };
  
  // Navigate to add item page
  const handleAddItem = () => {
    navigate("/items/new");
  };
  
  // Refresh stock data
  const handleRefresh = () => {
    setLoading(true);
    // The onValue listener will automatically refresh
    setTimeout(() => setLoading(false), 500);
  };
  
  // Get stock progress percentage
  const getStockProgress = (item) => {
    if (!item.maximumStock || item.maximumStock === 0) return 0;
    const percentage = (item.currentStock / item.maximumStock) * 100;
    return Math.min(percentage, 100);
  };
  
  return (
    <div className="stock-page">
      {/* Header */}
      <div className="page-header">
        <h2>📦 Stock Management</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/")} className="back-btn">
            🏠 Home
          </button>
          <button onClick={handleRefresh} className="refresh-btn">
            🔄 Refresh
          </button>
          <button onClick={handleAddItem} className="add-item-btn">
            + Add New Item
          </button>
        </div>
      </div>
      
      {/* Last Updated Info */}
      {lastUpdated && (
        <div className="last-updated">
          <span>🕐 Last updated: {lastUpdated}</span>
        </div>
      )}
      
      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">📦</div>
          <div className="stat-info">
            <div className="stat-label">Total Items</div>
            <div className="stat-value">{totalItems}</div>
          </div>
        </div>
        <div className="stat-card value">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <div className="stat-label">Stock Value</div>
            <div className="stat-value">{formatCurrency(totalStockValue)}</div>
          </div>
        </div>
        <div className="stat-card low">
          <div className="stat-icon">⚠️</div>
          <div className="stat-info">
            <div className="stat-label">Low Stock</div>
            <div className="stat-value">{lowStockItems}</div>
          </div>
        </div>
        <div className="stat-card out">
          <div className="stat-icon">❌</div>
          <div className="stat-info">
            <div className="stat-label">Out of Stock</div>
            <div className="stat-value">{outOfStockItems}</div>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by name, HSN, barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label>Category:</label>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Stock Status:</label>
          <select value={selectedStockStatus} onChange={(e) => setSelectedStockStatus(e.target.value)}>
            <option value="all">All Items</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
            <option value="normal">Normal Stock</option>
          </select>
        </div>
        
        <button 
          className="clear-filters"
          onClick={() => {
            setSearchTerm("");
            setSelectedCategory("all");
            setSelectedStockStatus("all");
          }}
        >
          Clear Filters
        </button>
      </div>
      
      {/* Stock Items List */}
      <div className="stock-items-container">
        <div className="table-header">
          <h3>Stock Items</h3>
          <span className="record-count">{filteredItems.length} items</span>
        </div>
        
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading stock items...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p>No items found</p>
            <button onClick={handleAddItem} className="create-btn">
              + Add New Item
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="stock-table">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Category</th>
                  <th>Current Stock</th>
                  <th>Min Stock</th>
                  <th>Max Stock</th>
                  <th>Unit</th>
                  <th>Purchase Price</th>
                  <th>Selling Price</th>
                  <th>Stock Value</th>
                  <th>Status</th>
                  <th>Actions</th>
                  </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => {
                  const stockStatus = getStockStatus(item);
                  const stockValue = (item.currentStock || 0) * (item.purchasePrice || 0);
                  const progress = getStockProgress(item);
                  
                  return (
                    <tr key={item.id} className={`stock-row ${stockStatus.class}`}>
                      <td className="item-name">
                        <strong>{item.name}</strong>
                        {item.hsn && <span className="hsn-code">HSN: {item.hsn}</span>}
                        {item.barcode && <span className="barcode">📷 {item.barcode}</span>}
                      </td>
                      <td>{item.category || "-"}</td>
                      <td className="stock-quantity">
                        <div className="stock-display">
                          <span className={`quantity ${stockStatus.class}`}>{item.currentStock || 0}</span>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${progress}%`, backgroundColor: stockStatus.color }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td>{item.minimumStock || 0}</td>
                      <td>{item.maximumStock || 0}</td>
                      <td>{item.unit || "NOS"}</td>
                      <td className="price">{formatCurrency(item.purchasePrice || 0)}</td>
                      <td className="price">{formatCurrency(item.sellingPrice || 0)}</td>
                      <td className="price stock-value">{formatCurrency(stockValue)}</td>
                      <td>
                        <span className={`status-badge ${stockStatus.class}`}>
                          {stockStatus.icon} {stockStatus.status}
                        </span>
                      </td>
                      <td className="actions">
                        <button 
                          className="action-btn adjust"
                          onClick={() => openAdjustModal(item)}
                          title="Adjust Stock"
                        >
                          🔄 Adjust
                        </button>
                        <button 
                          className="action-btn edit"
                          onClick={() => handleEditItem(item)}
                          title="Edit Item"
                        >
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="8"><strong>Total Stock Value</strong></td>
                  <td className="price"><strong>{formatCurrency(filteredItems.reduce((sum, item) => sum + ((item.currentStock || 0) * (item.purchasePrice || 0)), 0))}</strong></td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      
      {/* Stock Adjustment Modal */}
      {showAdjustModal && selectedItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Adjust Stock: {selectedItem.name}</h3>
              <button className="close-modal" onClick={() => setShowAdjustModal(false)}>✕</button>
            </div>
            
            <div className="current-stock-info">
              <div className="info-row">
                <span>Current Stock:</span>
                <strong>{selectedItem.currentStock || 0} {selectedItem.unit}</strong>
              </div>
              <div className="info-row">
                <span>Minimum Stock:</span>
                <span>{selectedItem.minimumStock || 0} {selectedItem.unit}</span>
              </div>
              <div className="info-row">
                <span>Maximum Stock:</span>
                <span>{selectedItem.maximumStock || 0} {selectedItem.unit}</span>
              </div>
            </div>
            
            <div className="adjustment-type">
              <label>Adjustment Type:</label>
              <div className="type-buttons">
                <button 
                  className={`type-btn ${adjustmentType === "add" ? "active" : ""}`}
                  onClick={() => setAdjustmentType("add")}
                >
                  ➕ Add Stock
                </button>
                <button 
                  className={`type-btn ${adjustmentType === "remove" ? "active" : ""}`}
                  onClick={() => setAdjustmentType("remove")}
                >
                  ➖ Remove Stock
                </button>
                <button 
                  className={`type-btn ${adjustmentType === "set" ? "active" : ""}`}
                  onClick={() => setAdjustmentType("set")}
                >
                  🔄 Set Stock
                </button>
              </div>
            </div>
            
            <div className="adjustment-quantity">
              <label>
                {adjustmentType === "add" && "Quantity to Add:"}
                {adjustmentType === "remove" && "Quantity to Remove:"}
                {adjustmentType === "set" && "New Stock Quantity:"}
              </label>
              <div className="quantity-input">
                <input
                  type="number"
                  value={adjustmentQty}
                  onChange={(e) => setAdjustmentQty(e.target.value)}
                  placeholder="Enter quantity"
                  step="1"
                  min="0"
                />
                <span className="unit">{selectedItem.unit}</span>
              </div>
            </div>
            
            <div className="adjustment-reason">
              <label>Reason / Notes:</label>
              <textarea
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                placeholder="Enter reason for stock adjustment (optional)"
                rows="2"
              />
            </div>
            
            {adjustmentType === "set" && adjustmentQty && (
              <div className="adjustment-preview">
                <div className="preview-row">
                  <span>New Stock:</span>
                  <strong>{adjustmentQty} {selectedItem.unit}</strong>
                </div>
                <div className="preview-row">
                  <span>Change:</span>
                  <span className={parseFloat(adjustmentQty) - (selectedItem.currentStock || 0) >= 0 ? "positive" : "negative"}>
                    {parseFloat(adjustmentQty) - (selectedItem.currentStock || 0) >= 0 ? "+" : ""}
                    {parseFloat(adjustmentQty) - (selectedItem.currentStock || 0)} {selectedItem.unit}
                  </span>
                </div>
              </div>
            )}
            
            {adjustmentType !== "set" && adjustmentQty && (
              <div className="adjustment-preview">
                <div className="preview-row">
                  <span>New Stock:</span>
                  <strong>
                    {adjustmentType === "add" 
                      ? (selectedItem.currentStock || 0) + parseFloat(adjustmentQty)
                      : (selectedItem.currentStock || 0) - parseFloat(adjustmentQty)
                    } {selectedItem.unit}
                  </strong>
                </div>
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowAdjustModal(false)}
                disabled={adjustmentLoading}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleAdjustStock}
                disabled={adjustmentLoading}
              >
                {adjustmentLoading ? "Processing..." : "Apply Adjustment"}
              </button>
            </div>
          </div>
        </div>
      )}
      
     {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      
      <style>{`
        .stock-page {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }
        
        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px 20px;
          background: white;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        
        .page-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }
        
        .back-btn, .refresh-btn, .add-item-btn {
          background: #f0f0f0;
          color: #333;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
          margin-left: 8px;
        }
        
        .refresh-btn {
          background: #28a745;
          color: white;
        }
        
        .add-item-btn {
          background: #5a60ea;
          color: white;
        }
        
        .last-updated {
          background: #e8f0fe;
          color: #5a60ea;
          padding: 8px 20px;
          margin: 0 20px 10px;
          border-radius: 8px;
          font-size: 12px;
          text-align: right;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          padding: 0 20px;
          margin: 20px 0;
        }
        
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .stat-icon {
          font-size: 32px;
        }
        
        .stat-info {
          flex: 1;
        }
        
        .stat-label {
          font-size: 11px;
          color: #666;
        }
        
        .stat-value {
          font-size: 18px;
          font-weight: bold;
        }
        
        .stat-card.total .stat-value { color: #5a60ea; }
        .stat-card.value .stat-value { color: #28a745; }
        .stat-card.low .stat-value { color: #ff9800; }
        .stat-card.out .stat-value { color: #dc3545; }
        
        .filters-section {
          background: white;
          margin: 0 20px 20px;
          padding: 15px;
          border-radius: 12px;
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          align-items: flex-end;
        }
        
        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
          flex: 1;
          min-width: 150px;
        }
        
        .filter-group label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
        }
        
        .filter-group input, .filter-group select {
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }
        
        .clear-filters {
          padding: 10px 20px;
          background: #f0f0f0;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }
        
        .stock-items-container {
          margin: 0 20px 20px;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid #eee;
        }
        
        .table-header h3 {
          margin: 0;
          font-size: 16px;
        }
        
        .record-count {
          font-size: 12px;
          color: #666;
        }
        
        .table-responsive {
          overflow-x: auto;
        }
        
        .stock-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .stock-table th,
        .stock-table td {
          padding: 12px 12px;
          text-align: left;
          border-bottom: 1px solid #f0f0f0;
          font-size: 13px;
        }
        
        .stock-table th {
          background: #fafafa;
          font-weight: 600;
          color: #333;
        }
        
        .item-name {
          min-width: 150px;
        }
        
        .hsn-code, .barcode {
          display: block;
          font-size: 10px;
          color: #999;
          margin-top: 2px;
        }
        
        .stock-quantity {
          min-width: 100px;
        }
        
        .stock-display {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        
        .quantity {
          font-weight: bold;
          font-size: 14px;
        }
        
        .quantity.in-stock {
          color: #28a745;
        }
        
        .quantity.low-stock {
          color: #ff9800;
        }
        
        .quantity.out-of-stock {
          color: #dc3545;
        }
        
        .progress-bar {
          width: 100%;
          height: 4px;
          background: #e0e0e0;
          border-radius: 2px;
          overflow: hidden;
        }
        
        .progress-fill {
          height: 100%;
          transition: width 0.3s;
        }
        
        .price {
          text-align: right;
        }
        
        .stock-value {
          font-weight: 500;
          color: #5a60ea;
        }
        
        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
        }
        
        .status-badge.in-stock {
          background: #e8f5e9;
          color: #28a745;
        }
        
        .status-badge.low-stock {
          background: #fff3e0;
          color: #ff9800;
        }
        
        .status-badge.out-of-stock {
          background: #ffebee;
          color: #dc3545;
        }
        
        .actions {
          white-space: nowrap;
        }
        
        .action-btn {
          padding: 5px 10px;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          margin: 0 2px;
          transition: all 0.2s;
        }
        
        .action-btn.adjust {
          background: #e3f2fd;
          color: #1976d2;
        }
        
        .action-btn.adjust:hover {
          background: #bbdef5;
        }
        
        .action-btn.edit {
          background: #f0f0f0;
          color: #666;
        }
        
        .action-btn.edit:hover {
          background: #e0e0e0;
        }
        
        .total-row {
          background: #f5f5f5;
          font-weight: bold;
        }
        
        .stock-row.low-stock {
          background: #fff8e7;
        }
        
        .stock-row.out-of-stock {
          background: #fff5f5;
        }
        
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 450px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #eee;
        }
        
        .modal-header h3 {
          margin: 0;
          font-size: 18px;
        }
        
        .close-modal {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #999;
        }
        
        .current-stock-info {
          padding: 15px 20px;
          background: #f8f9fa;
          margin: 0 20px 20px;
          border-radius: 8px;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 13px;
        }
        
        .adjustment-type {
          padding: 0 20px;
          margin-bottom: 20px;
        }
        
        .adjustment-type label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #333;
        }
        
        .type-buttons {
          display: flex;
          gap: 10px;
        }
        
        .type-btn {
          flex: 1;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 13px;
        }
        
        .type-btn.active {
          background: #5a60ea;
          color: white;
          border-color: #5a60ea;
        }
        
        .adjustment-quantity {
          padding: 0 20px;
          margin-bottom: 20px;
        }
        
        .adjustment-quantity label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #333;
        }
        
        .quantity-input {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .quantity-input input {
          flex: 1;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }
        
        .unit {
          padding: 10px;
          background: #f0f0f0;
          border-radius: 8px;
          font-size: 13px;
          color: #666;
        }
        
        .adjustment-reason {
          padding: 0 20px;
          margin-bottom: 20px;
        }
        
        .adjustment-reason label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #333;
        }
        
        .adjustment-reason textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 13px;
          resize: vertical;
        }
        
        .adjustment-preview {
          margin: 0 20px 20px;
          padding: 12px;
          background: #e8f0fe;
          border-radius: 8px;
        }
        
        .preview-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
          font-size: 13px;
        }
        
        .preview-row .positive {
          color: #28a745;
        }
        
        .preview-row .negative {
          color: #dc3545;
        }
        
        .modal-actions {
          display: flex;
          gap: 12px;
          padding: 20px;
          border-top: 1px solid #eee;
        }
        
        .btn-secondary, .btn-primary {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }
        
        .btn-secondary {
          background: #f0f0f0;
          color: #333;
        }
        
        .btn-primary {
          background: #5a60ea;
          color: white;
        }
        
        .btn-primary:disabled, .btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .loading-state, .empty-state {
          text-align: center;
          padding: 40px 20px;
        }
        
        .empty-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        
        .create-btn {
          margin-top: 15px;
          padding: 10px 20px;
          background: #5a60ea;
          color: white;
          border: none;
          border-radius: 20px;
          cursor: pointer;
        }
        
        .spinner {
          display: inline-block;
          width: 30px;
          height: 30px;
          border: 3px solid #f0f0f0;
          border-top-color: #5a60ea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 10px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }
        
        .bottom-nav div {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 20px;
        }
        
        .bottom-nav p {
          margin: 2px 0 0;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}